// frontend/src/components/UniversalPHICheck.jsx
// SIMPLIFIED VERSION - No more authentication loops

import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import PHIAccessModal from "./audit/PHIAccessModal";
import { phiAcknowledgmentApi } from "../utils/api";

const UniversalPHICheck = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Simple state management
  const [phiStatus, setPHIStatus] = useState({
    checking: true,
    hasAccess: false,
    showModal: false,
    error: null,
  });

  // Prevent duplicate API calls
  const checkInProgress = useRef(false);
  const hasChecked = useRef(false);

  // Single PHI access check function
  const checkPHIAccess = async () => {
    // Prevent duplicate calls
    if (checkInProgress.current || hasChecked.current) {
      return;
    }

    // Only check if authenticated
    if (!isAuthenticated || !user) {
      setPHIStatus({
        checking: false,
        hasAccess: true,
        showModal: false,
        error: null,
      });
      return;
    }

    checkInProgress.current = true;
    console.log("Checking PHI access for user:", user.user_id);

    try {
      const response = await phiAcknowledgmentApi.check("universal");

      if (response.data?.has_valid_acknowledgment) {
        console.log("Valid PHI acknowledgment found");
        setPHIStatus({
          checking: false,
          hasAccess: true,
          showModal: false,
          error: null,
        });
      } else {
        console.log("No valid PHI acknowledgment - showing modal");
        setPHIStatus({
          checking: false,
          hasAccess: false,
          showModal: true,
          error: null,
        });
      }

      hasChecked.current = true;
    } catch (error) {
      console.error("PHI access check failed:", error);

      // Handle different error types
      if (error.response?.status === 401) {
        // Auth error - don't show PHI modal, let auth system handle
        setPHIStatus({
          checking: false,
          hasAccess: false,
          showModal: false,
          error: "auth_error",
        });
      } else if (error.response?.status === 404) {
        // PHI system not found - allow access
        console.warn("PHI system not found - allowing access");
        setPHIStatus({
          checking: false,
          hasAccess: true,
          showModal: false,
          error: null,
        });
        hasChecked.current = true;
      } else {
        // Other errors - require acknowledgment for security
        console.warn("PHI check error - requiring acknowledgment");
        setPHIStatus({
          checking: false,
          hasAccess: false,
          showModal: true,
          error: error.message,
        });
        hasChecked.current = true;
      }
    } finally {
      checkInProgress.current = false;
    }
  };

  // Check PHI access when user changes
  useEffect(() => {
    if (!loading && isAuthenticated && user && !hasChecked.current) {
      // Small delay to ensure auth is stable
      const timeoutId = setTimeout(checkPHIAccess, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, user?.user_id, loading]);

  // Handle PHI acknowledgment confirmation
  const handleConfirm = async (accessData) => {
    try {
      console.log("Recording PHI acknowledgment...");

      await phiAcknowledgmentApi.record({
        moduleType: "universal",
        purpose: accessData.purpose,
        validityDays: 90,
        reason: accessData.reason,
      });

      setPHIStatus({
        checking: false,
        hasAccess: true,
        showModal: false,
        error: null,
      });

      toast.success("PHI access authorized for 90 days");
    } catch (error) {
      console.error("Failed to record PHI acknowledgment:", error);
      toast.error("Failed to authorize PHI access. Please try again.");
    }
  };

  const handleCancel = () => {
    console.log("PHI access cancelled");
    toast.info("PHI acknowledgment is required to access the system.");
    setPHIStatus((prev) => ({ ...prev, showModal: false }));
  };

  // Don't render anything while loading
  if (loading || !isAuthenticated || !user) {
    return children;
  }

  // Show loading spinner during initial check
  if (phiStatus.checking) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "200px" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Checking PHI access...</span>
          </div>
          <p className="mt-3">Verifying access permissions...</p>
        </div>
      </div>
    );
  }

  // Show PHI modal if needed
  if (phiStatus.showModal && !phiStatus.hasAccess) {
    return (
      <>
        {/* Render children with blur effect */}
        <div style={{ filter: "blur(2px)", pointerEvents: "none" }}>
          {children}
        </div>

        {/* PHI Modal overlay */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PHIAccessModal
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            entityType="System Access"
            entityId="universal-access"
          />
        </div>
      </>
    );
  }

  // Render children normally if has access or auth error
  return children;
};

export default UniversalPHICheck;
